import { useState, useEffect, useCallback } from 'react';
import { getAuth, getSentinelPasskeySetupUrl } from '../utils/auth';

const DISMISS_KEY = 'rk_passkey_dismissed';

function isDismissed(): boolean {
  return localStorage.getItem(DISMISS_KEY) === 'permanent';
}

function dismiss(): void {
  localStorage.setItem(DISMISS_KEY, 'permanent');
}

export default function PasskeyBanner() {
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Hide if browser doesn't support passkeys
    if (!window.PublicKeyCredential) return;
    // Hide if already dismissed
    if (isDismissed()) return;
    // Hide if user already has a passkey
    const auth = getAuth();
    if (!auth || auth.user.has_passkey !== false) return;
    setVisible(true);
  }, []);

  const handleDismiss = useCallback(() => {
    dismiss();
    setVisible(false);
  }, []);

  // Passkey enrollment MUST happen on the IdP origin (the WebAuthn RP-ID is the
  // IdP host, e.g. id-dev.responsekit.net). Running navigator.credentials.create()
  // here on the app host would bind the credential to the app origin and it would
  // be rejected at login. So send the user to the IdP's own /auth/passkey-setup
  // page (derived from the OIDC issuer). The IdP session cookie authenticates them
  // there; the page's "Back to sign in" link returns them to the app afterward.
  const handleRegister = useCallback(() => {
    const setupUrl = getSentinelPasskeySetupUrl();
    if (!setupUrl) {
      setError('Could not open passkey setup. Please sign out and back in, then try again.');
      return;
    }
    window.location.href = setupUrl;
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 199,
      background: 'var(--surface)', borderTop: '1px solid var(--border)',
      padding: '12px 16px',
      boxShadow: '0 -2px 12px rgba(0,0,0,0.15)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        maxWidth: 600, margin: '0 auto',
      }}>
        <div style={{ fontSize: 24, flexShrink: 0 }}>🔑</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            Sign in faster with a passkey
          </div>
          {error ? (
            <div style={{ fontSize: 12, color: 'var(--error, #dc2626)' }}>{error}</div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Use Face ID, fingerprint, or your device PIN instead of your password
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleRegister}
            style={{
              padding: '8px 16px', borderRadius: 'var(--radius)',
              background: 'var(--accent)', color: '#fff', border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Add passkey
          </button>
          <button
            onClick={handleDismiss}
            style={{
              padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'transparent', color: 'var(--text-muted)',
              border: 'none', fontSize: 18, cursor: 'pointer',
              lineHeight: 1,
            }}
            title="Dismiss"
          >
            &times;
          </button>
        </div>
      </div>
    </div>
  );
}
